import { Button, EmptyState, Skeleton } from '@personachat/ui';
import { useInfiniteQuery } from '@tanstack/react-query';
import { ClipboardList } from 'lucide-react';
import { Link } from 'react-router-dom';

import { api } from '../../api/client';
import { lire } from '../../api/call';
import type { components } from '../../api/schema';
import { ErreurChargement } from '../../components/bits';
import { formatDateTime, formatEuros } from '../../lib/format';

type Entree = components['schemas']['AuditEntry'];

/** Actions du journal en français ; une action inconnue affiche sa route. */
const ACTIONS: Record<string, string> = {
  'PATCH /api/admin/users/:id/type': 'Type de compte changé',
  'PATCH /api/admin/users/:id/toggle-status': 'Compte activé ou désactivé',
  'PUT /api/admin/users/:id/password': 'Lien de réinitialisation envoyé',
  'DELETE /api/admin/users/:id': 'Compte supprimé',
  'POST /api/admin/users/:id/restore': 'Compte restauré',
  'POST /api/admin/users/:id/verify-email': 'E-mail marqué comme vérifié',
  'POST /api/admin/users/:id/resend-verification': 'E-mail de vérification renvoyé',
  'POST /api/admin/users/:id/unlocks': 'Coach débloqué',
  'DELETE /api/admin/users/:id/unlocks/:personalityId': 'Coach retiré',
  'POST /api/admin/users/:userId/unlock-all': 'Tous les coachs débloqués',
  'POST /api/admin/billing/users/:id/credits': 'Crédits ajustés',
  'POST /api/admin/credits/bulk': 'Ajout groupé de crédits',
  'PUT /api/admin/settings/credits': 'Barème des crédits modifié',
  'POST /api/admin/access-keys': 'Clé d’accès créée',
  'POST /api/admin/access-keys/batch': 'Lot de clés d’accès créé',
  'PATCH /api/admin/access-keys/:id': 'Clé d’accès modifiée',
  'PATCH /api/admin/access-keys/:id/toggle': 'Clé d’accès activée ou désactivée',
  'DELETE /api/admin/access-keys/:id': 'Clé d’accès supprimée',
  'PATCH /api/admin/access-keys/bulk-activate': 'Clés d’accès activées',
  'PATCH /api/admin/access-keys/bulk-deactivate': 'Clés d’accès désactivées',
  'DELETE /api/admin/access-keys/bulk-delete': 'Clés d’accès supprimées',
  'POST /api/admin/unlock-keys': 'Clé de déblocage créée',
  'PUT /api/admin/unlock-keys/:id': 'Clé de déblocage modifiée',
  'DELETE /api/admin/unlock-keys/:id': 'Clé de déblocage supprimée',
  'POST /api/admin/unlock-keys/:id/reset-usage': 'Compteur de la clé remis à zéro',
  'POST /api/admin/billing/packs': 'Pack créé',
  'PATCH /api/admin/billing/packs/:id': 'Pack modifié',
  'POST /api/admin/billing/packs/:id/price': 'Prix du pack changé',
  'POST /api/admin/billing/plans': 'Formule créée',
  'PATCH /api/admin/billing/plans/:id': 'Formule modifiée',
  'POST /api/admin/billing/plans/:id/price': 'Prix de la formule changé',
  'POST /api/admin/events': 'Événement créé',
  'PATCH /api/admin/events/:id': 'Événement modifié',
  'PUT /api/admin/events/:id/schedule': 'Calendrier de l’événement modifié',
  'POST /api/admin/events/:id/preview-key': 'Clé de prévisualisation générée',
  'DELETE /api/admin/events/:id': 'Événement supprimé',
  'POST /api/admin/catalog/modules': 'Module créé',
  'PATCH /api/admin/catalog/modules/:id': 'Module modifié',
  'DELETE /api/admin/catalog/modules/:id': 'Module supprimé',
  'PUT /api/admin/catalog/modules/order': 'Ordre des modules changé',
  'PUT /api/admin/catalog/modules/:id/coaches/order': 'Ordre des coachs changé',
  'POST /api/admin/catalog/coaches': 'Coach créé',
  'PATCH /api/admin/catalog/coaches/:id': 'Coach modifié',
  'POST /api/admin/catalog/coaches/:id/avatar': 'Avatar du coach changé',
  'DELETE /api/admin/catalog/coaches/:id/avatar': 'Avatar du coach retiré',
  'PUT /api/admin/catalog/coaches/:id/prompt': 'Prompt publié',
  'POST /api/admin/catalog/coaches/:id/prompt-versions/:versionId/restore': 'Ancienne version du prompt restaurée',
  'POST /api/admin/catalog/coaches/:id/test': 'Conversation d’essai avec le coach',
  'POST /api/admin/modules': 'Module créé (ancien back-office)',
  'PUT /api/admin/modules/:id': 'Module modifié (ancien back-office)',
  'DELETE /api/admin/modules/:id': 'Module supprimé (ancien back-office)',
  'POST /api/admin/modules/reorder': 'Ordre des modules changé (ancien back-office)',
  'POST /api/admin/personalities': 'Coach créé (ancien back-office)',
  'PUT /api/admin/personalities/:id': 'Coach modifié (ancien back-office)',
  'POST /api/admin/personalities/reorder': 'Ordre des coachs changé (ancien back-office)',
  'POST /api/admin/prompts/update': 'Prompts rechargés depuis les fichiers',
  'PUT /api/admin/prompts/:id': 'Prompt modifié (ancien back-office)',
  'POST /api/admin/users/:id/conversations/:conversationId/read': 'Conversation lue',
  'POST /api/admin/users/:id/memories/read': 'Mémoire IA lue',
  'PATCH /api/admin/users/:id/memories': 'Mémoire IA corrigée',
  'POST /api/admin/users/:id/export': 'Export des données (RGPD)',
  'POST /api/admin/admins': 'Administrateur créé',
  'POST /api/admin/admins/invite': 'Administrateur invité',
  'POST /api/admin/admins/promote': 'Compte élève transformé en administrateur',
  'POST /api/admin/admins/:id/invitation': 'Invitation renvoyée',
  'PATCH /api/admin/admins/:id': 'Administrateur modifié',
  'PUT /api/admin/settings/security': 'Réglage de la double authentification',
  'POST /api/auth/2fa/verify': 'Connexion avec code (double authentification)',
  'POST /api/auth/2fa/setup': 'Enrôlement de la double authentification commencé',
  'POST /api/auth/2fa/enable': 'Double authentification activée',
  'POST /api/auth/2fa/recovery-codes': 'Codes de secours renouvelés',
  'POST /api/auth/2fa/disable': 'Double authentification désactivée',
  'POST /api/admin/mcp/demande/approuver': 'Assistant IA autorisé (MCP)',
  'DELETE /api/admin/mcp/connexions/:id': 'Accès d’un assistant IA retiré (MCP)',
};

export function libelleAction(action: string): string {
  return ACTIONS[action] ?? action.replace(/^(\w+) \/api\/(admin|auth)/, '$1 ');
}

const CHAMPS: Record<string, string> = {
  userType: 'Type',
  solde: 'Solde',
  delta: 'Mouvement',
  isActive: 'Actif',
  isEmailVerified: 'E-mail vérifié',
  deletedAt: 'Supprimé le',
  maxUses: 'Utilisations max',
  expiresAt: 'Expiration',
  description: 'Description',
  coach: 'Coach',
  debloque: 'Débloqué',
  debloqueLe: 'Débloqué le',
  retires: 'Coachs retirés',
  debloques: 'Coachs débloqués',
  montant: 'Crédits par élève',
  credites: 'Élèves créditées',
  dejaFaits: 'Déjà créditées',
  echecs: 'Échecs',
  total: 'Total',
  costs: 'Coûts',
  key: 'Code',
  usedCount: 'Utilisations',
  signupGrants: 'Offerts à l’inscription',
  name: 'Nom',
  code: 'Code',
  credits: 'Crédits',
  creditsPerPeriod: 'Crédits par période',
  priceCents: 'Prix',
  abonneesAncienPrix: 'Abonnées gardant l’ancien prix',
  module: 'Module',
  moduleId: 'Module',
  startsAt: 'Début',
  endsAt: 'Fin',
  endBehavior: 'Après la fin',
  endMessage: 'Message de fin',
  endCtaLabel: 'Bouton de fin',
  endCtaUrl: 'Lien du bouton',
  previewKey: 'Clé de prévisualisation',
  eventId: 'Événement',
  coachsOuverts: 'Coachs ouverts aux inscrites',
  questionsCount: 'Questions',
  welcomeMessage: 'Message d’accueil',
  canGenerateImages: 'Génère des images',
  canUseMemory: 'Lit la mémoire',
  canEnrichMemory: 'Complète la mémoire',
  tutorialVideoUrl: 'Tutoriel (lien)',
  tutorialVideoTitle: 'Tutoriel (titre)',
  isSuperAdmin: 'Super admin',
  isAdmin: 'Admin',
  doubleAuth: 'Double authentification',
  doubleAuthObligatoire: 'Double auth. obligatoire (super admins)',
  email: 'E-mail',
  superAdmin: 'Super admin',
  invitationEnvoyee: 'Invitation envoyée',
  category: 'Catégorie',
  displayOrder: 'Position',
  personalityOrder: 'Position',
  modules: 'Modules',
  coaches: 'Coachs',
  aiModel: 'Modèle',
  reasoningEffort: 'Raisonnement',
  avatar: 'Avatar',
  version: 'Version',
  versionId: 'Version',
  note: 'Note',
  longueur: 'Longueur du prompt',
  source: 'Origine',
  conversation: 'Conversation',
  projet: 'Projet',
  section: 'Section',
  messages: 'Messages',
  application: 'Application',
  retour: 'Retour vers',
  ordre: 'Ordre',
  systemPrompt: 'Prompt',
  personalityId: 'Coach',
  versionRestauree: 'Version restaurée',
  nouvelleVersion: 'Nouvelle version',
  titre: 'Titre',
  memoire: 'Mémoire',
  lignes: 'Lignes',
  action: 'Action',
  projets: 'Projets',
  medias: 'Médias',
};

const VALEURS: Record<string, string> = {
  NORMAL: 'Normal',
  GUEST: 'Invité',
  CHALLENGE3: 'Immersion 3',
  OPEN: 'reste ouvert',
  BLOCK: 'bloqué',
};

function valeur(v: unknown, champ = ''): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'number' && champ.endsWith('Cents')) return formatEuros(v);
  if (typeof v === 'string' && v in VALEURS) return VALEURS[v]!;
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) return formatDateTime(v);
  if (typeof v === 'boolean') return v ? 'oui' : 'non';
  if (Array.isArray(v)) return v.length ? v.join(', ') : '—';
  // Un prompt entier n'a pas sa place dans une ligne du journal : sa longueur suffit.
  if (typeof v === 'string' && v.length > 120) return `${v.slice(0, 100)}… (${v.length} caractères)`;
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/** « Type : Invité → Normal » pour chaque champ connu avant et après. */
export function differences(avant: unknown, apres: unknown): string[] {
  const a = (avant && typeof avant === 'object' ? avant : {}) as Record<string, unknown>;
  const b = (apres && typeof apres === 'object' ? apres : {}) as Record<string, unknown>;
  const cles = [...new Set([...Object.keys(a), ...Object.keys(b)])];
  return cles.flatMap((c) => {
    const nom = CHAMPS[c] ?? c;
    // Une valeur inchangée (le coach concerné, par exemple) n'est rappelée qu'une fois.
    if (c in a && c in b && valeur(a[c], c) === valeur(b[c], c)) return [`${nom} : ${valeur(b[c], c)}`];
    if (c in a && c in b) return [`${nom} : ${valeur(a[c], c)} → ${valeur(b[c], c)}`];
    return [c in b ? `${nom} : ${valeur(b[c], c)}` : `${nom} : ${valeur(a[c], c)} (avant)`];
  });
}

/**
 * Actions admin faites sur un objet (onglet « Journal » d'une fiche,
 * cahier des charges §7.12), les plus récentes d'abord.
 */
export function AuditTimeline({ targetId }: { targetId: string }) {
  const journal = useInfiniteQuery({
    queryKey: ['audit', targetId],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => lire(api.GET('/api/admin/audit', { params: { query: { targetId, page: pageParam, limit: 25 } } })),
    getNextPageParam: (d) => (d.pagination.page < d.pagination.pages ? d.pagination.page + 1 : undefined),
  });

  if (journal.isError) return <ErreurChargement erreur={journal.error} onReessayer={() => void journal.refetch()} />;
  if (journal.isLoading) {
    return (
      <div className="stack">
        <Skeleton height={40} />
        <Skeleton height={40} />
        <Skeleton height={40} />
      </div>
    );
  }

  const entrees: Entree[] = journal.data?.pages.flatMap((p) => p.entries) ?? [];
  if (entrees.length === 0) {
    return <EmptyState icon={<ClipboardList size={26} aria-hidden="true" />} title="Aucune action admin" text="Les actions faites depuis le back-office apparaîtront ici." />;
  }

  return (
    <>
      <ol className="timeline">
        {entrees.map((e) => {
          const echec = e.statusCode >= 400;
          const diff = differences(e.before, e.after);
          return (
            <li key={e.id} className={echec ? 'timeline__item timeline__item--echec' : 'timeline__item'}>
              <span className="timeline__date">{formatDateTime(e.createdAt)}</span>
              <div>
                <div className="timeline__action">
                  <span className="timeline__label">{libelleAction(e.action)}</span>
                  {echec && <span className="muted small"> — refusé par l’API ({e.statusCode})</span>}
                </div>
                <div className="timeline__meta">
                  {e.adminEmail ?? 'Admin inconnu'}
                  {e.channel === 'mcp' && <span className="badge-mcp" title="Action faite par un assistant IA connecté au compte (MCP)">MCP</span>}
                  {e.reason && <> · « {e.reason} »</>}
                </div>
                {diff.length > 0 && <div className="timeline__diff">{diff.join(' · ')}</div>}
              </div>
            </li>
          );
        })}
      </ol>
      <div className="row" style={{ marginTop: 8 }}>
        {journal.hasNextPage && (
          <Button size="sm" onClick={() => void journal.fetchNextPage()} loading={journal.isFetchingNextPage}>
            Voir plus
          </Button>
        )}
        <Link className="link small" to={`/journal?cible=${encodeURIComponent(targetId)}`}>
          Ouvrir dans le journal d’audit
        </Link>
      </div>
    </>
  );
}
